import type { ApolloCache, Reference } from '@apollo/client'

/**
 * `cache.modify` factory for the two filter-and-decrement patterns used
 * by Community library mutations.
 *
 * Both today's `useProfilePlaylists.handleRemovePlaylistItem` and
 * `useFollowedPlaylists.handleRemoveFollowedPlaylist` follow the same
 * shape — read the field, filter Reference[] / edges by matching the
 * node's id, decrement a sibling counter or one nested inside the
 * connection. This factory captures that shape so each call site
 * shrinks to a 3-line invocation, the inline `{ __ref }`-typed
 * `existing` parameter disappears, and the two paths stay in sync.
 */

type ReadFieldFn = <T = unknown>(fieldName: string, ref: Reference | undefined) => T | undefined

export type RemoveItemMatch = (ref: Reference, readField: ReadFieldFn) => boolean

interface CountInsideConnection {
  totalCount?: number
  itemCount?: number
}

interface ConnectionExisting extends CountInsideConnection {
  edges?: Array<{ node: Reference; [k: string]: unknown }>
  // Apollo retains arbitrary additional fields on the connection (pageInfo,
  // __typename, etc.) — preserved via object spread.
  [k: string]: unknown
}

export interface CreateRemoveItemUpdaterOptions {
  /**
   * Cache entity id. Either `'ROOT_QUERY'` for root fields, or the result
   * of `cache.identify(...)` for an entity-scoped field. When the caller
   * passes `undefined` (entity not in the cache yet), the returned updater
   * is a no-op — protects against ill-timed mutations on un-cached entities.
   */
  entityId: string | undefined
  /** Field name on the entity to mutate (e.g. `'playlistItems'`, `'followedPlaylists'`). */
  field: string
  /**
   * Optional dotted-path within the field at which the array lives — set to
   * `'edges'` when the field is a connection. When omitted, `field` is
   * treated as a flat `Reference[]`.
   */
  edgesPath?: 'edges'
  /** Optional sibling/connection counter to decrement (e.g. `'itemCount'`, `'totalCount'`). */
  countField?: string
  /**
   * Where the counter lives relative to the matched array.
   * `'sibling'` → a sibling field on the same parent entity (e.g. `Playlist.itemCount`).
   * `'within-connection'` → a field inside the connection object itself
   *  (e.g. `followedPlaylists.totalCount`). Required when `countField` is set.
   */
  countLocation?: 'sibling' | 'within-connection'
  /**
   * Predicate against the candidate Reference (or `edge.node` for connections).
   * Return true to REMOVE. `readField` is Apollo's reactive field accessor —
   * use it (`readField('id', ref)`) instead of `ref.id` because edges store
   * normalized refs, not inlined nodes.
   */
  match: RemoveItemMatch
}

type CacheUpdater = (cache: ApolloCache<unknown>) => void

/**
 * Build a `cache.modify` updater for a remove-and-decrement pattern.
 *
 * Two call shapes covered (matches the two pre-refactor call sites):
 *
 * ```ts
 * // Flat-array + sibling counter (Playlist.playlistItems + Playlist.itemCount)
 * createRemoveItemUpdater({
 *   entityId: cache.identify({ __typename: 'Playlist', id: playlistId }),
 *   field: 'playlistItems',
 *   countField: 'itemCount',
 *   countLocation: 'sibling',
 *   match: (ref, readField) => String(readField('id', ref)) === String(playlistItemId),
 * })(cache)
 *
 * // Connection + within-connection counter (ROOT_QUERY.followedPlaylists)
 * createRemoveItemUpdater({
 *   entityId: 'ROOT_QUERY',
 *   field: 'followedPlaylists',
 *   edgesPath: 'edges',
 *   countField: 'totalCount',
 *   countLocation: 'within-connection',
 *   match: (ref, readField) => readField('id', ref) === playlistId,
 * })(cache)
 * ```
 */
export function createRemoveItemUpdater (
  options: CreateRemoveItemUpdaterOptions,
): CacheUpdater {
  const {
    entityId, field, edgesPath, countField, countLocation, match,
  } = options

  return (cache: ApolloCache<unknown>) => {
    // Entity not yet in the cache → no-op. Defensive: removes a class of
    // races where the mutation fires before the read populates the entry.
    if (!entityId) return

    cache.modify({
      id: entityId,
      fields: {
        [field]: (existing: unknown, modifierArgs: { readField: ReadFieldFn }) => {
          const { readField } = modifierArgs

          if (edgesPath) {
            // Connection branch — `existing` shaped as { edges, pageInfo, totalCount, ... }.
            const conn = existing as ConnectionExisting | undefined
            if (!conn?.edges || !Array.isArray(conn.edges)) return existing
            const nextEdges = conn.edges.filter((edge) => !match(edge.node, readField))
            if (nextEdges.length === conn.edges.length) return existing

            // Decrement within-connection counter when configured.
            let nextConn: ConnectionExisting = { ...conn, edges: nextEdges }
            if (countField && countLocation === 'within-connection') {
              const prev = (conn as Record<string, unknown>)[countField]
              if (typeof prev === 'number') {
                nextConn = { ...nextConn, [countField]: Math.max(0, prev - 1) }
              }
            }
            return nextConn
          }

          // Flat-array branch — `existing` shaped as readonly Reference[].
          if (!Array.isArray(existing)) return existing
          const next = (existing as readonly Reference[]).filter(
            (ref) => !match(ref, readField),
          )
          if (next.length === existing.length) return existing
          return next
        },

        // Sibling counter on the same parent entity, decremented in the same
        // modify() call so the read returns a consistent (count, array) pair.
        ...(countField && countLocation === 'sibling'
          ? {
            [countField]: (existing: unknown) => {
              const prev = typeof existing === 'number' ? existing : 0
              return Math.max(0, prev - 1)
            },
          }
          : {}),
      },
    })
  }
}
